import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { Loader2, ShieldCheck, KeyRound, Smartphone, Users, Copy } from "lucide-react";

export default function AccountSecurityModal({ onClose }) {
  const [tab, setTab] = useState("password");
  const tabs = [
    { key: "password", label: "Password", Icon: KeyRound },
    { key: "twofa", label: "Two-factor", Icon: Smartphone },
    { key: "colleagues", label: "Colleagues", Icon: Users },
  ];
  return (
    <Modal title="Account & security" onClose={onClose}>
      <div className="flex gap-1.5 mb-5 border-b border-slate-200 -mt-1">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} data-testid={`security-tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold font-['Outfit'] border-b-2 -mb-px transition ${
              tab === key ? "border-[#4A5D6B] text-[#4A5D6B]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === "password" && <ChangePassword />}
      {tab === "twofa" && <TwoFactor />}
      {tab === "colleagues" && <Colleagues />}
    </Modal>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <Field label="Current password">
        <input data-testid="cur-password-input" type="password" value={cur} onChange={(e) => setCur(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
      </Field>
      <Field label="New password">
        <input data-testid="new-password-input" type="password" value={next} onChange={(e) => setNext(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
        <p className="text-xs text-slate-400 mt-1.5">At least 8 characters, including a letter and a number.</p>
      </Field>
      <button data-testid="change-password-btn" onClick={submit} disabled={saving || !cur || !next}
        className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] transition flex items-center justify-center gap-2 disabled:opacity-50">
        {saving && <Loader2 size={16} className="animate-spin" />} Update password
      </button>
    </div>
  );
}

function TwoFactor() {
  const [status, setStatus] = useState(null); // {enabled, backup_codes_remaining}
  const [setup, setSetup] = useState(null);   // {secret, qr}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disable, setDisable] = useState(null); // {password, code}

  const load = () => api.get("/auth/2fa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  // Mount-once: load 2FA status on first render only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code: code.trim() });
      setBackupCodes(data.backup_codes); setSetup(null); setCode("");
      toast.success("Two-factor authentication enabled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disable.password, code: disable.code.trim() });
      toast.success("Two-factor authentication disabled");
      setDisable(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (status === null) return <div className="flex justify-center py-6 text-slate-300"><Loader2 className="animate-spin" /></div>;

  if (backupCodes) return (
    <div data-testid="backup-codes-block">
      <div className="flex items-center gap-2 text-emerald-700 mb-2"><ShieldCheck size={18} /><p className="font-semibold font-['Outfit']">Two-factor is on</p></div>
      <p className="text-sm text-slate-500 mb-3">Save these one-time backup codes somewhere safe. Each can be used once if you lose your phone.</p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {backupCodes.map((c) => <span key={c} className="font-mono text-sm bg-[#F2F2F2] rounded-lg px-3 py-2 text-center tracking-wider text-slate-700">{c}</span>)}
      </div>
      <button data-testid="copy-backup-codes-btn" onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Backup codes copied"); }}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 hover:border-[#7FB069] transition">
        <Copy size={15} /> Copy codes
      </button>
    </div>
  );

  if (disable) return (
    <div className="space-y-4" data-testid="twofa-disable-form">
      <p className="text-sm text-slate-500">Confirm your password and a current code to turn off two-factor authentication.</p>
      <Field label="Password">
        <input data-testid="disable-password-input" type="password" value={disable.password}
          onChange={(e) => setDisable({ ...disable, password: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
      </Field>
      <Field label="Authentication code">
        <input data-testid="disable-code-input" inputMode="numeric" value={disable.code}
          onChange={(e) => setDisable({ ...disable, code: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
      </Field>
      <div className="flex gap-2">
        <button onClick={() => setDisable(null)} className="flex-1 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
        <button data-testid="confirm-disable-2fa-btn" onClick={doDisable} disabled={busy}
          className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white py-2.5 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Turn off
        </button>
      </div>
    </div>
  );

  if (setup) return (
    <div className="space-y-3" data-testid="twofa-setup-form">
      <p className="text-sm text-slate-500">Scan this QR code with Google Authenticator, Microsoft Authenticator or 1Password, then enter the 6-digit code to confirm.</p>
      <div className="flex justify-center"><img src={setup.qr} alt="2FA QR code" className="h-44 w-44" data-testid="twofa-qr" /></div>
      <p className="text-xs text-slate-400 text-center">Can't scan? Enter this key manually:<br /><span className="font-mono text-slate-600 break-all">{setup.secret}</span></p>
      <Field label="6-digit code">
        <input data-testid="twofa-verify-input" inputMode="numeric" autoFocus value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-center tracking-[0.4em] text-lg outline-none focus:border-[#81B29A]" placeholder="123456" />
      </Field>
      <button data-testid="verify-2fa-btn" onClick={verify} disabled={busy || code.length < 6}
        className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] transition flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 size={16} className="animate-spin" />} Verify & enable
      </button>
    </div>
  );

  return (
    <div data-testid="twofa-status-block">
      {status.enabled ? (
        <>
          <div className="flex items-center gap-2 text-emerald-700 mb-1"><ShieldCheck size={18} /><p className="font-semibold font-['Outfit']">Two-factor is on</p></div>
          <p className="text-sm text-slate-500 mb-4">Your account asks for a code from your authenticator app at sign-in. {status.backup_codes_remaining} backup code(s) remaining.</p>
          <button data-testid="disable-2fa-btn" onClick={() => setDisable({ password: "", code: "" })}
            className="w-full rounded-full border border-red-200 text-red-600 hover:bg-red-50 py-2.5 text-sm font-semibold transition">Turn off two-factor</button>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-4">Add a second layer of security. You'll enter a code from an authenticator app each time you sign in.</p>
          <button data-testid="enable-2fa-btn" onClick={startSetup} disabled={busy}
            className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] transition flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Smartphone size={16} />} Set up two-factor
          </button>
        </>
      )}
    </div>
  );
}

function Colleagues() {
  const [list, setList] = useState(null);
  const [target, setTarget] = useState(null); // colleague obj
  const [pw, setPw] = useState({ new_password: "", my_password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/colleagues").then((r) => setList(r.data)).catch(() => setList([])); }, []);

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(`/colleagues/${target.id}/reset-password`, pw);
      toast.success(`Password reset for ${target.name}`);
      setTarget(null); setPw({ new_password: "", my_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (list === null) return <div className="flex justify-center py-6 text-slate-300"><Loader2 className="animate-spin" /></div>;

  if (target) return (
    <div className="space-y-4" data-testid="colleague-reset-form">
      <p className="text-sm text-slate-500">Set a temporary password for <b>{target.name}</b> ({target.email}). Ask them to change it after signing in.</p>
      <Field label="New password for colleague">
        <input data-testid="colleague-new-password-input" type="text" value={pw.new_password}
          onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
        <p className="text-xs text-slate-400 mt-1.5">At least 8 characters, including a letter and a number.</p>
      </Field>
      <Field label="Your password (to confirm it's you)">
        <input data-testid="colleague-my-password-input" type="password" value={pw.my_password}
          onChange={(e) => setPw({ ...pw, my_password: e.target.value })}
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
      </Field>
      <div className="flex gap-2">
        <button onClick={() => setTarget(null)} className="flex-1 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
        <button data-testid="confirm-colleague-reset-btn" onClick={reset} disabled={busy || !pw.new_password || !pw.my_password}
          className="flex-1 rounded-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white py-2.5 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Reset password
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="colleagues-list">
      <p className="text-sm text-slate-500 mb-4">Reset the password for a colleague you share a class with — handy if they're locked out and there's no email set up.</p>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No colleagues yet. Share a class with a colleague to see them here.</p>
      ) : (
        <div className="space-y-2">
          {list.map((c) => (
            <div key={c.id} data-testid={`colleague-row-${c.id}`} className="flex items-center justify-between rounded-xl border border-slate-100 px-3.5 py-3">
              <div>
                <p className="font-semibold font-['Outfit'] text-slate-800 text-sm">{c.name}</p>
                <p className="text-xs text-slate-400">{c.email}</p>
              </div>
              <button data-testid={`reset-colleague-${c.id}`} onClick={() => setTarget(c)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-[#7FB069] transition">
                <KeyRound size={13} /> Reset password
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
